// My Day — the landing page, for one person.
//
// The dashboard that used to sit here answered "how is the firm doing":
// matter counts, a stage funnel, attorney workload, AI spend. That is a
// real question and it still has a page (Firm Overview), but it is not
// the question somebody has when they open the app in the morning. This
// one answers "what am I supposed to do next."
//
// Same signals as Today, narrowed to the person signed in. Nothing here
// is written by a model — every line is computed from the database, and
// each carries the fact that put it on the list.
//
// THE FAILURE THIS PAGE HAS TO AVOID. Work reaches a person by matching
// names, and names are the least reliable join in the app: a deadline's
// responsible user is free text, and the matter's responsible attorney
// comes from Clio. If they do not match the signed-in user's name, this
// page renders an empty, calm, reassuring screen while the work sits in
// somebody else's pile. So when the firm has items and this person has
// none, the page says which of those two things happened.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch, cachedFetch, clearApiCache } from "../api.js";
import { TodayPage } from "./TodayPage.jsx";
import { Spinner } from "../components/Spinner.jsx";
import { SignalRow, KIND_LABELS } from "../components/SignalRow.jsx";
import { PendingApprovalCard } from "../components/PendingApprovalCard.jsx";
import { CoveragePanel } from "../components/CoveragePanel.jsx";

// Quick actions, by who is looking.
//
// An attorney's landing page is their caseload and their dates.
// Generating documents and running conflict checks is paralegal work,
// and offering it to a partner is clutter on the page they open every
// morning — Aaron said so directly. Paralegals keep the tooling,
// because for them it IS the work.
const QUICK_ACTIONS_BY_ROLE = {
  lawyer: [
    { id: "deadlines", label: "All deadlines", icon: I.clock },
    { id: "matters", label: "All matters", icon: I.folder },
    { id: "new-matter", label: "New matter", icon: I.plus },
  ],
  staff: [
    { id: "generate", label: "Generate document", icon: I.zap },
    { id: "conflicts", label: "Conflict check", icon: I.search },
    { id: "deadlines", label: "All deadlines", icon: I.clock },
    { id: "matters", label: "All matters", icon: I.folder },
  ],
};

const LAWYER_ROLES = new Set(["associate", "attorney", "partner",
                              "firm_admin", "platform_operator"]);

// Relative where that reads better, absolute once it stops being
// obvious — "at 05:00" is clearer than "14 hours ago" by the afternoon.
function formatWhen(iso) {
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} minutes ago`;
  const sameDay = then.toDateString() === new Date().toDateString();
  const time = then.toLocaleTimeString(undefined,
    { hour: "2-digit", minute: "2-digit" });
  return sameDay ? `at ${time}` : `${then.toLocaleDateString()} at ${time}`;
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export function MyDayPage({ authStatus, onNavigate, onMatterClick,
                            initialScope = "mine" }) {
  const role = authStatus?.firm_user?.role || "pending";
  const isPending = role === "pending";
  const actions = QUICK_ACTIONS_BY_ROLE[
    LAWYER_ROLES.has(role) ? "lawyer" : "staff"];
  const me = authStatus?.user?.name || authStatus?.firm_user?.name || "";

  // "Today" used to be a separate nav item showing the same brief
  // unnarrowed. Two destinations with near-synonymous names, and
  // nothing in either name saying which was which. It is a scope on
  // this page now: the distinction is mine-versus-everyone's, and a
  // toggle says that where a pair of names could not.
  const [scope, setScope] = useState(initialScope);
  const [refreshing, setRefreshing] = useState(false);
  const [showAllMatters, setShowAllMatters] = useState(false);
  const [brief, setBrief] = useState(null);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (isPending) return;
    setError(null);
    try {
      // ONE call, not two.
      //
      // This fetched the firm-wide brief as well, purely to tell an
      // empty personal list apart from an unmatched name. It did not
      // need to: narrowing to a person filters `people` and leaves
      // `totals` firm-wide, so the answer was already in the response.
      // The second call was a whole extra brief build — including a
      // second Clio pass over every open matter — on every load of the
      // landing page.
      //
      // Cached briefly so moving between pages and coming back does not
      // rebuild it. This is a summary of the day, not a live feed;
      // a minute old is fine, and the Refresh path still forces it.
      setBrief(await cachedFetch("/api/daily-brief?mine=true"));
    } catch (e) {
      setError(e.message);
      setBrief({ people: [], totals: {}, coverage: {} });
    }
    try {
      const qs = new URLSearchParams({ days: "7" });
      if (me) qs.set("user_name", me);
      setDone(await cachedFetch(`/api/firm-feed?${qs.toString()}`));
    } catch {
      setDone(null);
    }
  }, [isPending, me]);

  useEffect(() => { load(); }, [load]);

  // The brief is built overnight, so somebody who has just finished a
  // task needs a way to see it gone rather than being told to wait
  // until tomorrow. Clears the client cache too — refreshing into a
  // sixty-second-old copy would look broken.
  const refresh = async () => {
    setRefreshing(true);
    try {
      clearApiCache("/api/daily-brief");
      setBrief(await apiFetch("/api/daily-brief?mine=true&refresh=true"));
    } catch (e) {
      setError(e.message);
    }
    setRefreshing(false);
  };

  const block = useMemo(
    () => (brief?.people || []).find((p) => !p.is_unassigned) || null,
    [brief],
  );

  if (isPending) return React.createElement(PendingApprovalCard, { authStatus });

  if (brief === null) {
    return React.createElement("div", {
      style: { padding: "32px 40px", display: "flex", gap: "8px", alignItems: "center" },
    },
      React.createElement(Spinner, { size: 16 }),
      React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
        "Working out what needs doing…"));
  }

  const signals = block?.signals || [];
  // Firm-wide even though the brief is narrowed to one person —
  // `totals` is deliberately not filtered, which is what makes the
  // second request unnecessary.
  const firmTotal = brief?.totals?.signals ?? null;
  const coverage = brief.coverage || {};
  // No block at all — not merely an empty one. A person with matters
  // but nothing due now gets a block, so an empty list is genuinely a
  // quiet week rather than a broken name. Keying this on "no signals"
  // instead would tell a lawyer having a good day that their account is
  // misconfigured.
  const unmatched = !block && firmTotal > 0;

  return React.createElement("div", { style: { padding: "32px 40px", maxWidth: "980px" } },
    React.createElement("h1", {
      style: { fontFamily: "'Playfair Display', serif", fontSize: "30px",
               color: C.navy, marginBottom: "4px" },
    }, me ? `${greeting()}, ${me.split(" ")[0]}` : greeting()),
    React.createElement("p", {
      style: { fontSize: "14px", color: C.textMuted, marginBottom: "22px" },
    }, signals.length === 0
        ? "Nothing is assigned to you right now."
        : `${signals.length} thing${signals.length === 1 ? "" : "s"} need your attention`
          + (block.total_signals > signals.length
              ? ` — showing the top ${signals.length} of ${block.total_signals}.` : ".")),

    // WHEN this was worked out, stated rather than implied. The brief
    // is built overnight and can be most of a day old by design; a
    // figure with no timestamp is read as live, and somebody who
    // finished a task an hour ago would think the app had lost it.
    brief.generated_at && React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", alignItems: "center", gap: "10px",
               fontSize: "11.5px", color: C.textLight, marginBottom: "18px",
               flexWrap: "wrap" },
    },
      React.createElement("span", null,
        `Worked out ${formatWhen(brief.generated_at)}`,
        brief.was_manual ? " (you refreshed it)" : "",
        ". Anything finished since then is not reflected."),
      React.createElement("button", {
        onClick: refresh, disabled: refreshing,
        style: { border: "none", background: "transparent", padding: 0,
                 fontSize: "11.5px", color: C.navy,
                 cursor: refreshing ? "default" : "pointer",
                 textDecoration: "underline" },
      }, refreshing ? "Working it out…" : "Rebuild now"),
    ),

    React.createElement("div", {
      style: { display: "flex", gap: "8px", marginBottom: "20px" },
    },
      ...[["mine", "Mine"], ["firm", "Whole firm"]].map(([k, label]) =>
        React.createElement("button", {
          key: k,
          onClick: () => setScope(k),
          style: {
            border: `1px solid ${scope === k ? C.navy : C.warmGray}`,
            background: scope === k ? C.navy : "#fff",
            color: scope === k ? "#fff" : C.navy,
            borderRadius: "7px", padding: "6px 15px", fontSize: "12.5px",
            fontWeight: 600, cursor: "pointer",
          },
        }, label)),
    ),

    scope === "firm"
      ? React.createElement(TodayPage, { onMatterClick, embedded: true })
      : React.createElement(React.Fragment, null,

    error && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "16px" },
    }, error),

    // Your numbers, not the firm's.
    //
    // Counted server-side over ALL of your signals rather than the rows
    // rendered below — counting what is on screen would shrink every
    // figure by exactly the amount the item cap hid, which is the
    // opposite of what a summary is for.
    //
    // No colour coding. "Past due: 2" is already the strongest thing
    // this page can say; painting it red adds a threshold nobody set.
    block && React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", gap: "28px", flexWrap: "wrap",
               padding: "4px 0 18px", borderBottom: `1px solid ${C.warmGray}`,
               marginBottom: "22px" },
    },
      ...[
        ["Your matters", block.counts?.matters,
         "Open matters where you are the responsible attorney"],
        ["Gone quiet", block.counts?.matters_quiet,
         "Yours with no activity in either system for long enough to notice"],
        ["Needing attention", block.counts?.total,
         "Everything on your list, including anything trimmed below"],
        ["Tasks", block.counts?.tasks,
         "Steps from the matter template applied to your matters"],
        ["Past due", block.counts?.past_due,
         "Already overdue, and still open"],
        ["Statutory", block.counts?.jurisdictional,
         "Statutes and notice-of-claim windows — the ones that end cases"],
      ].map(([label, n, title]) => React.createElement("div", {
        key: label, title,
      },
        React.createElement("div", {
          style: { fontSize: "22px", fontWeight: 700, color: C.navy,
                   fontFamily: "'Playfair Display',serif",
                   fontVariantNumeric: "tabular-nums" },
        }, n == null ? "—" : String(n)),
        React.createElement("div", {
          style: { fontSize: "11px", color: C.textMuted,
                   textTransform: "uppercase", letterSpacing: "0.06em" },
        }, label),
      )),
    ),

    block?.counts?.tasks_undated > 0 && React.createElement("p", {
      style: { fontSize: "11.5px", color: C.textMuted, marginTop: "-10px",
               marginBottom: "16px", lineHeight: 1.5 },
    }, `You also have ${block.counts.tasks_undated} task`
     + `${block.counts.tasks_undated === 1 ? "" : "s"} with no due date. `
     + "Real work, just undated — listing them here would bury everything "
     + "that has a date. They are on the matter."),

    // The single date most worth knowing, stated rather than left to be
    // found in the list — it is the one that cannot be recovered from.
    block?.next_jurisdictional && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "18px", lineHeight: 1.5 },
    },
      React.createElement("strong", null, "Next statutory date: "),
      block.next_jurisdictional.headline,
      block.next_jurisdictional.matter_label
        && React.createElement("span", { style: { color: C.textMuted } },
             ` (${block.next_jurisdictional.matter_label})`),
    ),

    (coverage.degraded || []).length > 0 && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "16px", lineHeight: 1.5 },
    },
      React.createElement("strong", null, "This is incomplete. "),
      coverage.degraded.join(" "),
    ),

    // ── My work ──────────────────────────────────────────────────
    signals.length > 0
      ? React.createElement("div", {
          style: { background: "#fff", border: `1px solid ${C.warmGray}`,
                   borderRadius: "10px", overflow: "hidden", marginBottom: "12px" },
        },
          ...signals.map((s, i) => React.createElement(SignalRow, {
            key: s.id, signal: s, first: i === 0, onMatterClick,
          })),
        )
      : React.createElement("div", {
          style: { background: "#fff", border: `1px solid ${C.warmGray}`,
                   borderRadius: "12px", padding: "24px", fontSize: "14px",
                   color: C.textMuted, lineHeight: 1.6, marginBottom: "12px" },
        },
          unmatched
            // The dangerous case, said out loud. An empty personal list
            // beside a firm that has work is more often a name that did
            // not match than a person with nothing to do.
            ? React.createElement("div", null,
                React.createElement("div", {
                  style: { color: C.textDark, fontWeight: 600, marginBottom: "8px" },
                }, "Nothing is attributed to you — but the firm has "
                 + `${firmTotal} item${firmTotal === 1 ? "" : "s"}.`),
                React.createElement("div", null,
                  "Work reaches a person by name: the deadline's own "
                  + "responsible user, then the matter's responsible "
                  + "attorney in Clio. If neither matches ",
                  React.createElement("strong", null, me || "your account"),
                  ", your work will sit in someone else's list or in the "
                  + "unassigned pile. Do not read this as being clear."),
                React.createElement("button", {
                  onClick: () => onNavigate && onNavigate("today"),
                  style: { marginTop: "12px", border: "none",
                           background: "transparent", padding: 0,
                           fontSize: "13px", color: C.navy, cursor: "pointer",
                           textDecoration: "underline" },
                }, "See the whole firm's list"),
              )
            : brief.no_records_anywhere
              ? "There is nothing here to draw on yet — no deadlines, "
                + "conflict checks, reviews or treatment records exist. "
                + "That is not the same as being up to date."
              : "Nothing of yours is past due or falling due in the "
                + `next ${brief.horizon_days} days. This is a summary, `
                + "not a docket — the deadline list has the full picture."),

    block && Object.keys(block.trimmed || {}).length > 0
      && React.createElement("p", {
        style: { fontSize: "11.5px", color: C.textMuted, marginBottom: "26px",
                 lineHeight: 1.5 },
      }, "Not shown, to keep this readable: "
       + Object.entries(block.trimmed)
           .map(([k, n]) => `${n} ${(KIND_LABELS[k] || k).toLowerCase()}`)
           .join(", ")
       + ". Statutory dates are never held back."),

    // ── Quick actions ────────────────────────────────────────────
    React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", gap: "10px", flexWrap: "wrap",
               margin: "24px 0 30px" },
    },
      ...actions.map((a) => React.createElement("button", {
        key: a.id,
        onClick: () => onNavigate && onNavigate(a.id),
        style: {
          display: "flex", alignItems: "center", gap: "8px",
          border: `1px solid ${C.warmGray}`, background: "#fff",
          borderRadius: "8px", padding: "10px 15px", fontSize: "13px",
          fontWeight: 600, color: C.navy, cursor: "pointer",
        },
      }, a.icon, a.label)),
    ),

    // ── Your matters ─────────────────────────────────────────────
    //
    // The caseload, quietest first. A count you cannot open is the same
    // problem as the task count Aaron asked to be able to click, and
    // for an attorney this list IS the page.
    (block?.matters || []).length > 0 && React.createElement("div", {
      style: { marginBottom: "30px" },
    },
      React.createElement("div", {
        style: { display: "flex", alignItems: "baseline", gap: "10px",
                 marginBottom: "10px" },
      },
        React.createElement("h2", {
          style: { fontSize: "13px", color: C.navy, fontWeight: 700,
                   textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 },
        }, "Your matters"),
        React.createElement("div", { style: { flex: 1, height: "1px", background: C.warmGray } }),
        React.createElement("span", {
          style: { fontSize: "11px", color: C.textLight },
        }, `${block.matters.length}, quietest first`),
      ),
      React.createElement("div", {
        style: { background: "#fff", border: `1px solid ${C.warmGray}`,
                 borderRadius: "10px", overflow: "hidden" },
      },
        ...(showAllMatters ? block.matters : block.matters.slice(0, 10))
          .map((m, i) => React.createElement("div", {
            key: m.matter_id,
            className: "stack-row",
            style: { display: "flex", gap: "12px", alignItems: "baseline",
                     padding: "10px 15px",
                     borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}55` },
          },
            React.createElement("button", {
              onClick: () => onMatterClick
                && onMatterClick({ id: Number(m.matter_id) }),
              style: { border: "none", background: "transparent", padding: 0,
                       fontSize: "13px", color: C.navy, cursor: "pointer",
                       textDecoration: "underline", fontWeight: 600,
                       flexShrink: 0 },
            }, m.display_number),
            React.createElement("span", {
              style: { flex: 1, minWidth: 0, fontSize: "13px",
                       color: C.textDark, overflow: "hidden",
                       textOverflow: "ellipsis", whiteSpace: "nowrap" },
            }, m.client_name),
            m.stage && React.createElement("span", {
              style: { fontSize: "11.5px", color: C.textMuted, flexShrink: 0 },
            }, m.stage),
            React.createElement("span", {
              style: { fontSize: "11.5px", color: C.textLight, flexShrink: 0,
                       minWidth: "84px", textAlign: "right" },
            }, m.days_idle == null ? "" : `quiet ${m.days_idle}d`),
          )),
      ),
      block.matters.length > 10 && React.createElement("button", {
        onClick: () => setShowAllMatters((v) => !v),
        style: { border: "none", background: "transparent", padding: "7px 0",
                 fontSize: "12px", color: C.navy, cursor: "pointer",
                 textDecoration: "underline" },
      }, showAllMatters ? "Show fewer"
                        : `Show all ${block.matters.length}`),
    ),

    // ── What I have been doing ───────────────────────────────────
    React.createElement("div", null,
      React.createElement("div", {
        style: { display: "flex", alignItems: "baseline", gap: "10px",
                 marginBottom: "10px" },
      },
        React.createElement("h2", {
          style: { fontSize: "13px", color: C.navy, fontWeight: 700,
                   textTransform: "uppercase", letterSpacing: "0.06em", margin: 0 },
        }, "Your last seven days"),
        React.createElement("div", { style: { flex: 1, height: "1px", background: C.warmGray } }),
      ),
      React.createElement("div", {
        style: { background: "#fff", border: `1px solid ${C.warmGray}`,
                 borderRadius: "10px", overflow: "hidden" },
      },
        ...(function () {
          const items = [];
          for (const day of done?.days || []) {
            for (const it of day.items || []) items.push(it);
          }
          if (items.length === 0) {
            return [React.createElement("div", {
              key: "none",
              style: { padding: "20px", fontSize: "13px", color: C.textMuted,
                       lineHeight: 1.55 },
            }, me
                ? `No case work recorded for ${me} in the last seven days. `
                  + "Only work done through this app appears here — anything "
                  + "done directly in Clio does not."
                : "No recent activity recorded.")];
          }
          return items.slice(0, 10).map((it, i) => React.createElement("div", {
            key: it.id || i,
            className: "stack-row",
            style: {
              display: "flex", gap: "12px", alignItems: "baseline",
              padding: "10px 15px", fontSize: "13px", color: C.textDark,
              borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}55`,
            },
          },
            React.createElement("span", { style: { flex: 1, minWidth: 0 } },
              it.description),
            it.matter_name && React.createElement("span", {
              style: { fontSize: "12px", color: C.textMuted, flexShrink: 0 },
            }, it.matter_name),
            React.createElement("span", {
              style: { fontSize: "11px", color: C.textLight, flexShrink: 0 },
            }, it.time),
          ));
        })(),
      ),
    ),
      // Same disclosure as the firm view. This is the page somebody
      // lands on, so it is the one most likely to be sat in front of
      // unexplained.
      React.createElement(CoveragePanel, { coverage }),
      ),
  );
}
